import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { apiGetRegions, apiGetCities, apiGetCountries } from './apiAccount';
import { readCache, writeCache } from 'lib/indexedDbCache';
import { ADDRESS_CACHE_TTL, ADDRESS_CACHE_KEY, ADDRESS_RESPONSE_KEY } from '../constants/cache/addressCache';
import { responseErrorHandling } from '../services/utilityService';
import { filterAndSortCountries } from '../containers/Ezlo/EzloAccount/MyAccount/countriesUtils';
import { loadAccountUsersWithUuids } from '../helpers/account/loadAccountUsersWithUuids';
import { LOGOUT } from '../constants/ActionTypes';

/**
 * Validates an address reference-data response and returns the expected list.
 *
 * Throws when the backend signals a logical error (HTTP 200 with `status === ERROR_STATUS`) or when
 * the payload shape changed and the requested field is no longer an array.
 *
 * @param {Object} response - Axios response from an address endpoint.
 * @param {string} listKey - Field under `response.data` holding the list (e.g. 'Regions', 'Cities').
 * @returns {Array} The validated list.
 * @throws {Error} If the response signals an error or the field is not an array.
 */
const extractAddressList = (response, listKey) => {
    responseErrorHandling(response);

    const list = response?.data?.[listKey];
    if (!Array.isArray(list)) {
        throw new Error(`Unexpected address response format: "${listKey}" is not an array`);
    }

    return list;
};

/**
 * Resolves a list cache-first: returns a fresh cached value without hitting the network; otherwise
 * fetches, overwrites the cache and returns the fresh value. If the network fails but we still have
 * a (stale) cached value, we return it rather than surfacing an error.
 *
 * @param {Object} params
 * @param {string} params.key - IndexedDB cache key.
 * @param {number} params.ttlMs - Freshness window for the cached value.
 * @param {Function} params.fetchData - Async function returning the fresh list from the network.
 * @returns {Promise<{data: *} | {error: {message: string}}>} An RTK Query queryFn result.
 */
const resolveCachedList = async ({ key, ttlMs, fetchData }) => {
    const cached = await readCache(key, ttlMs);
    if (cached && !cached.isStale) {
        return { data: cached.data };
    }

    try {
        const data = await fetchData();
        await writeCache(key, data);

        return { data };
    } catch (error) {
        if (cached) {
            return { data: cached.data };
        }

        return { error: { message: error?.message } };
    }
};

/**
 * RTK Query slice for account reference data: address lists (countries, regions, cities) and the
 * account users with their resolved UUIDs.
 *
 * Each endpoint caches its result in IndexedDB while RTK Query layers in-session deduplication,
 * request state and automatic re-rendering on top.
 */
export const accountApi = createApi({
    reducerPath: 'accountApi',
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        getCountries: builder.query({
            queryFn: () =>
                resolveCachedList({
                    key: ADDRESS_CACHE_KEY.countries,
                    ttlMs: ADDRESS_CACHE_TTL.COUNTRIES,
                    fetchData: async () => {
                        const response = await apiGetCountries();

                        return filterAndSortCountries(extractAddressList(response, ADDRESS_RESPONSE_KEY.COUNTRIES));
                    },
                }),
        }),
        getRegions: builder.query({
            queryFn: (PK_Country) =>
                resolveCachedList({
                    key: ADDRESS_CACHE_KEY.regions(PK_Country),
                    ttlMs: ADDRESS_CACHE_TTL.REGIONS,
                    fetchData: async () => {
                        const response = await apiGetRegions(PK_Country);

                        return extractAddressList(response, ADDRESS_RESPONSE_KEY.REGIONS);
                    },
                }),
        }),
        getCities: builder.query({
            queryFn: ({ PK_Country, PK_Region }) =>
                resolveCachedList({
                    key: ADDRESS_CACHE_KEY.cities(PK_Country, PK_Region),
                    ttlMs: ADDRESS_CACHE_TTL.CITIES,
                    fetchData: async () => {
                        const response = await apiGetCities(PK_Country, PK_Region);

                        return extractAddressList(response, ADDRESS_RESPONSE_KEY.CITIES);
                    },
                }),
        }),
        getAccountUsersWithUuid: builder.query({
            queryFn: async () => {
                try {
                    return { data: await loadAccountUsersWithUuids() };
                } catch (error) {
                    return { error: { message: error?.message } };
                }
            },
        }),
    }),
});

export const { useGetCountriesQuery, useGetRegionsQuery, useGetCitiesQuery, useGetAccountUsersWithUuidQuery } =
    accountApi;

/**
 * accountApi's reducer, extended to reset its cache on logout so a new account never sees the
 * previous account's cached data (the account-users query has no args, so its cache is shared across
 * accounts). Registered in the root reducer instead of the raw `accountApi.reducer`.
 */
export const accountApiReducer = (state, action) =>
    action.type === LOGOUT ? accountApi.reducer(undefined, action) : accountApi.reducer(state, action);
